import React from 'react'
import PropTypes from 'prop-types'
import Table from 'react-bootstrap/Table'
import TableLoading from 'shared/tables/table-loading'
import TableFooter from 'shared/tables/table-footer'
import MutedText from 'shared/forms/muted-text'
import TableStats from './TableStats'
import Badge from 'react-bootstrap/Badge'
import { useNavigate } from 'react-router-dom'
import SortArrow from 'shared/tables/sort-arrow'
import TableError from 'shared/tables/table-error'
import useDutyRoles from 'api/hooks/use-duty-roles'
import { InternalConflict, RoleScoreBadge } from 'shared/badges'

function RoleTable({ filterParams, setFilterParams }) {
  const navigate = useNavigate()

  const updateOrdering = (column) => {
    const isCurrentColumn = filterParams.orderBy === column
    const columnOrder = getColumnOrder(isCurrentColumn)

    setFilterParams({
      ...filterParams,
      orderBy: column,
      orderDirection: columnOrder,
      page: 1
    })
  }

  const getColumnOrder = (isCurrentColumn) => {
    if (isCurrentColumn) {
      return filterParams.orderDirection === 'asc' ? 'desc' : 'asc'
    } else {
      return 'desc'
    }
  }

  const {
    isSuccess,
    isLoading,
    isError,
    data: roles
  } = useDutyRoles({ params: filterParams, options: { refetchOnWindowFocus: false } })

  return (
    <>
      {isSuccess && (
        <TableStats
          params={filterParams}
          setParams={setFilterParams}
          roles={roles}
        />
      )}
      <Table
        hover
        size="sm"
        className="border shadow-sm bg-white"
        style={{ cursor: 'pointer' }}
      >
        <thead>
          <tr>
            <th colSpan="3" className="text-center border-end">
              Role
            </th>
            <th colSpan="3" className="text-center border-end">
              Counts
            </th>
            <th colSpan="2" className="text-center border-right">
              Permissions
            </th>
          </tr>
          <tr>
            <th
              className="border-left border-right"
              onClick={() => updateOrdering('name')}
              style={{ cursor: 'pointer' }}
            >
              Name
              <SortArrow
                column="name"
                orderBy={filterParams.orderBy}
                orderDirection={filterParams.orderDirection}
              />
            </th>
            <th
              className="border-right"
              onClick={() => updateOrdering('role_sod_counts.score')}
              style={{ cursor: 'pointer' }}
            >
              Score
              <SortArrow
                column="role_sod_counts.score"
                orderBy={filterParams.orderBy}
                orderDirection={filterParams.orderDirection}
              />
            </th>
            <th style={{ width: '7%', cursor: 'pointer' }}>Status</th>
            <th
              style={{ width: '15%', cursor: 'pointer' }}
              onClick={() =>
                updateOrdering('role_sod_counts.internal_conflicts_count')
              }
            >
              Internal Conflicts
              <SortArrow
                column="role_sod_counts.internal_conflicts_count"
                orderBy={filterParams.orderBy}
                orderDirection={filterParams.orderDirection}
              />
            </th>
            <th
              style={{ width: '10%', cursor: 'pointer' }}
              onClick={() => updateOrdering('role_sod_counts.employees_count')}
            >
              Users
              <SortArrow
                column="role_sod_counts.employees_count"
                orderBy={filterParams.orderBy}
                orderDirection={filterParams.orderDirection}
              />
            </th>
            <th style={{ width: '10%', cursor: 'pointer' }}>
              Functional Categories
            </th>
            <th
              style={{ width: '10%', cursor: 'pointer' }}
              onClick={() =>
                updateOrdering('role_sod_counts.high_risk_permissions_count')
              }
            >
              High Risk
              <SortArrow
                column="role_sod_counts.high_risk_permissions_count"
                orderBy={filterParams.orderBy}
                orderDirection={filterParams.orderDirection}
              />
            </th>
            <th
              style={{ width: '10%', cursor: 'pointer' }}
              onClick={() =>
                updateOrdering('role_sod_counts.superuser_permissions_count')
              }
            >
              Super User
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading columns={8} />}
          {isSuccess &&
            roles.data.map((d) => (
              <tr
                key={`role-row-${d.id}`}
                className="border-right"
                onClick={() => navigate(`/roles/${d.id}`)}
              >
                <td>
                  {d.name}

                  {d.caption && <div className="text-muted">{d.caption}</div>}
                  {d.business_rules_count > 0 && (
                    <Badge
                      pill
                      className={
                        'text-center fw-normal bg-light text-primary mx-1 border border-light'
                      }
                    >
                      {d.business_rules_count} Business Rule(s)
                    </Badge>
                  )}
                </td>
                <td className="text-center">
                  <RoleScoreBadge count={d.score} showCount={true} />
                </td>
                <td className="text-center">
                  <span className="text-muted">{d.status}</span>
                </td>
                <td className="text-center">
                  {d.internal_conflicts > 0 && (
                    <InternalConflict count={d.internal_conflicts} />
                  )}
                </td>
                <td className="text-center text-muted">{d.employees_count}</td>
                <td className="text-center text-muted">
                  {Object.keys(d.functional_categories || {}).length}
                </td>
                <td className="text-center">
                  <div className="text-muted">
                    {d.high_risk_permissions_count}
                  </div>
                </td>
                <td className="text-center">
                  <div className="text-muted">
                    {d.superuser_permissions_count}
                  </div>
                </td>
              </tr>
            ))}
          {isSuccess && roles.data.length === 0 && (
            <tr>
              <td colSpan="8" className="text-center py-4">
                <div className="mb-2 h6">No Roles Found</div>
                <MutedText as="div" lighter className="mb-3">
                  Remove or adjust filters
                </MutedText>
              </td>
            </tr>
          )}
          {isError && <TableError columns={8} />}
        </tbody>
      </Table>
      {isSuccess && (
        <TableFooter
          meta={roles.meta}
          params={filterParams}
          setParams={setFilterParams}
        />
      )}
    </>
  )
}

RoleTable.propTypes = {
  filterParams: PropTypes.object.isRequired,
  setFilterParams: PropTypes.func.isRequired
}

export default RoleTable
